/**
 * Lazy singleton loader for the OpenCV.js WASM runtime.
 *
 * The bundle is ~10 MB (the WASM is base64-inlined, so there is no second fetch),
 * which is far too heavy to put on the image-upscaler page's critical path. It is
 * therefore loaded on demand the first time the user actually asks for a
 * watermark to be removed, and cached for every later use.
 *
 * OpenCV.js is a UMD bundle whose export is an emscripten "thenable" that only
 * settles once the WASM runtime has finished initialising. Awaiting it naively
 * wedges the page, so both `importRuntime()` and `settle()` are written the long
 * way around; each carries a note explaining what it is dodging.
 */

/** Minimal shape of an OpenCV Mat we actually touch. */
export interface OpenCvMat {
  data: Uint8Array;
  rows: number;
  cols: number;
  delete(): void;
}

export interface OpenCvRuntime {
  Mat: new (rows?: number, cols?: number, type?: number) => OpenCvMat;
  inpaint: (
    src: OpenCvMat,
    mask: OpenCvMat,
    dst: OpenCvMat,
    radius: number,
    flags: number
  ) => void;
  INPAINT_TELEA: number;
  INPAINT_NS: number;
  CV_8UC1: number;
  CV_8UC3: number;
  CV_8UC4: number;
}

let pending: Promise<void> | null = null;
let runtime: OpenCvRuntime | null = null;

import { registerOpenCvProvider } from './openCvProvider';


/**
 * An already-acquired module (the emscripten Module, not yet resolved).
 *
 * Exists so the Node-side verification script can inject a module it required
 * directly, since `importRuntime()` is browser-only. Production code should
 * leave this alone.
 */
let injectedModule: unknown = null;

/** Supply an already-required OpenCV module instead of importing one. */
export function injectOpenCvModule(module: unknown): void {
  injectedModule = module;
  pending = null;
}

function isRuntime(value: unknown): value is OpenCvRuntime {
  return (
    !!value &&
    typeof (value as OpenCvRuntime).inpaint === 'function' &&
    typeof (value as OpenCvRuntime).Mat === 'function'
  );
}

/** True once the runtime has finished initialising. */
export function isOpenCvReady(): boolean {
  return runtime !== null;
}

/**
 * Synchronously hand back the ready runtime.
 *
 * This is deliberately *not* the return value of `loadOpenCv`. See the note on
 * `settle()` for why the emscripten Module must never be passed through a
 * promise resolution.
 */
export function getOpenCv(): OpenCvRuntime {
  if (!runtime) {
    throw new Error('OpenCV runtime requested before it was ready. Await loadOpenCv() first.');
  }
  return runtime;
}

/**
 * Load and initialise the runtime, resolving once it is ready to use.
 *
 * Resolves with `void` deliberately — see `settle()` for why the emscripten
 * Module must never travel through a promise resolution.
 */
export function loadOpenCv(): Promise<void> {
  if (pending) return pending;
  pending = (async () => {
    if (injectedModule !== null) {
      return settle(pickModule(injectedModule as Record<string, unknown>));
    }
    await importRuntime();
    return settle(pickModule(importedModule as Record<string, unknown>));
  })();

  return pending;
}

/**
 * Vendor URL for the raw OpenCV UMD bundle.
 *
 * `scripts/vendor-opencv.ts` copies the file out of node_modules into
 * `public/vendor/` on `predev`/`prebuild`, so Vite serves it verbatim in dev
 * and ships it in `dist/` on build.
 */
const VENDOR_URL = '/vendor/opencv/opencv.js';

/**
 * Set by `importRuntime()`.
 *
 * The imported value has to be parked in module scope rather than returned from
 * a promise, because a thenable passed through a promise resolution sends V8
 * into an unbounded loop.
 */
let importedModule: unknown = null;

/** In-flight script load, so concurrent callers share one request. */
let scriptPromise: Promise<void> | null = null;

/**
 * Load the raw OpenCV UMD bundle and capture the `window.cv` it installs.
 *
 * Loaded as a classic `<script>` rather than via `import()` on purpose. The
 * package is UMD whose export is an emscripten Module (a thenable), and both
 * bundler paths break on that:
 *
 *   - `await import(...)` resolves *with* the thenable, so V8's promise
 *     resolution loops forever and the renderer pegs the CPU with no way out.
 *   - Vite additionally wraps dynamic imports in `__vitePreload`, which hung
 *     here: the import never settled and the engine never became ready.
 *
 * As a plain script the UMD assigns `window.cv` itself, which is exactly the
 * vendor's documented usage. The value is captured into module scope rather than
 * resolved with, for the thenable reason above.
 */
function importRuntime(): Promise<void> {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const cv = (window as { cv?: unknown }).cv;
    if (isRuntime(cv) || typeof (cv as { then?: unknown } | undefined)?.then === 'function') {
      // Already on the page (e.g. a second mount after the first load).
      importedModule = cv;
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = VENDOR_URL;
    script.async = true;
    script.onload = () => {
      const loaded = (window as { cv?: unknown }).cv;
      if (!loaded) {
        reject(
          new Error(
            'The inpainting engine loaded but did not install itself. Try refreshing the page.'
          )
        );
        return;
      }
      // Park the Module in module scope and resolve with `void`. Resolving
      // *with* it would hand the thenable to the promise machinery, which then
      // follows `Module.then` recursively and spins the renderer at 100% CPU
      // forever. This is the single easiest way to hang this page.
      importedModule = loaded;
      resolve();
    };
    script.onerror = () => {
      scriptPromise = null;
      reject(
        new Error('The inpainting engine could not be loaded. Check your connection and try again.')
      );
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * The bundle is a UMD whose export is the emscripten Module object itself, and
 * that object is a "thenable" that only settles once the WASM runtime has
 * finished initialising. Interop therefore has two possible shapes:
 *
 *   - raw module namespace (Node/CommonJS): the namespace *is* the thenable
 *     Module, and there is no `.default`.
 *   - bundler ESM interop: the thenable is parked on `.default`.
 *
 * Only reach for `.default` when the namespace is neither already a usable
 * runtime nor thenable — otherwise we'd overwrite a perfectly good module with
 * `undefined`.
 */
function pickModule(namespace: Record<string, unknown>): unknown {
  const isDirect =
    isRuntime(namespace) ||
    typeof (namespace as { then?: unknown } | undefined)?.then === 'function';
  if (isDirect) return namespace;
  return (namespace as { default?: unknown }).default ?? namespace;
}

/** Generous ceiling for a one-time WASM download + compile. */
const RUNTIME_INIT_TIMEOUT_MS = 60_000;

/**
 * Initialise an emscripten Module, storing the result for `getOpenCv()`.
 *
 * The emscripten Module is a thenable in three separate ways, and every one of
 * them turns into an infinite loop if you treat it like a normal promise:
 *
 *  1. `Module.then` *returns the Module itself*. So `await module` makes the
 *     resolution loop call `.then` on itself forever. We therefore ignore
 *     `then`'s return value and resolve from inside its callback.
 *
 *  2. The value that callback receives is the Module. Passing it to `resolve`
 *     is fine *only* because we resolve with `undefined` and stash the Module
 *     in the module-level `runtime`.
 *
 *  3. Returning the Module from any `.then` callback is fatal — the promise
 *     machinery treats the return value as a resolution value, sees a thenable,
 *     and chases it forever (synchronously, so even timers stop firing and the
 *     failure looks like a hang rather than an error).
 *
 * Resolving with `void` and exposing `getOpenCv()` structurally prevents (3).
 */
function settle(module: unknown): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const fail = (message: string) => {
      pending = null;
      runtime = null;
      reject(new Error(message));
    };

    const timer = setTimeout(
      () => fail('The inpainting engine took too long to start. Please try again.'),
      RUNTIME_INIT_TIMEOUT_MS
    );

    const thenable = module as
      | { then?: (onReady: (resolved: unknown) => void) => unknown }
      | undefined;

    if (typeof thenable?.then !== 'function') {
      clearTimeout(timer);
      if (isRuntime(module)) {
        runtime = module;
        resolve();
      } else {
        fail('The inpainting engine loaded but did not expose a usable OpenCV runtime.');
      }
      return;
    }
    thenable.then((resolved: unknown) => {
      clearTimeout(timer);
      if (isRuntime(resolved)) {
        runtime = resolved;
        resolve();
      } else {
        fail('The inpainting engine loaded but did not expose a usable OpenCV runtime.');
      }
    });
  });
}

/**
 * Reset the cached runtime. Only used by tests; production callers should let
 * the singleton live for the lifetime of the page.
 */
export function resetOpenCvForTests(): void {
  pending = null;
  runtime = null;
  importedModule = null;
  scriptPromise = null;
}

// Register the browser loader so `inpaint.ts` can acquire the runtime without
// knowing how it was loaded. Importing this module has no side effects beyond
// that — in particular it never touches `document` until a load is requested,
// which is what lets `scripts/verify-inpaint.ts` import it under Node.
registerOpenCvProvider(loadOpenCv, getOpenCv);
